import ButtonComponent from '@ticketmaster/tm1pos-web-shared/components/ButtonComponent';
import { PricingDetails } from '@ticketmaster/tm1pos-web-shared/components/v2/Financial/PricingDetails/PricingDetails';
import { PricingDetailsItem } from '@ticketmaster/tm1pos-web-shared/components/v2/Financial/PricingDetailsItem/PricingDetailsItem';
import mainMessages from '@ticketmaster/tm1pos-web-shared/messages';
import { type CurrentEventDetails } from '@ticketmaster/tm1pos-web-shared/model/current-event-details';
import { type Message } from '@ticketmaster/tm1pos-web-shared/src/model/message';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { toggleEditOtherChargesModal } from '../../../App/event-fees-actions';
import ShoppingBagItem from '../../../EventDetailPage/components/ShoppingBagItem';
import { selectCurrentEventFees } from '../../../EventDetailPage/selectors/main';
import messages from './checkout-summary.messages';
import EventInfo from './components/event-info/event-info';
import type { DeliverySeat } from '../../../../models/delivery-seat';
import type { DeliveryTicketType } from '../../../../models/delivery-ticket-type';
import type { DeliveryTicketTypeInfo } from '../../../../models/delivery-ticket-type-info';
import type { Charge, Price } from '@ticketmaster/tm1pos-web-shared/model/graphql';

import './checkout-summary.scss';

type Items = {
  [key: string]: {
    details: CurrentEventDetails;
  };
};

type DeliverySeatWithPromotion = DeliverySeat & {
  type: DeliveryTicketTypeInfo & { current: DeliveryTicketType & { promotion: string } };
};

type SeatBlocks = {
  [key: string]: { seats: DeliverySeatWithPromotion[] }[];
};

type FeesAndTaxes = {
  amount: number;
  currency: string;
};

type CheckoutSummaryTotals = {
  feesAndTaxesBreakdown: Charge[];
  feesAndTaxes: FeesAndTaxes;
  faceValue: Price;
  eventFees: Price;
  eventFeesBreakdown: Charge[];
  total: Price;
};

type CheckoutSummaryProps = {
  isDeselectingSeat: boolean;
  removingSeatPlacedId: string;
  isOrderProcessed: boolean;
  onClearCart: () => void;
  isOrderUnpaid: boolean;
  onDeselectSeat: () => void;
  checkoutSeatBlocks: SeatBlocks;
  eventSeatBlocks: SeatBlocks;
  headerMsg: Message;
  items: Items;
  totals: CheckoutSummaryTotals;
};

export const CheckoutSummary = (props: CheckoutSummaryProps) => {
  const dispatch = useDispatch();
  const currentEventFees = useSelector(selectCurrentEventFees);

  const {
    isDeselectingSeat,
    removingSeatPlacedId,
    isOrderProcessed,
    onClearCart,
    isOrderUnpaid,
    onDeselectSeat,
    checkoutSeatBlocks = {},
    eventSeatBlocks = {},
    headerMsg,
    items,
    totals,
  } = props;

  const toggleEditChargesModal = () => {
    dispatch(toggleEditOtherChargesModal());
  };

  const checkoutSummaryClassName = classNames({
    'sdr-checkout__summary': true,
    'sdr-checkout__summary--gray': !isOrderProcessed,
  });

  const showFeesTitle = !!totals.feesAndTaxesBreakdown.length || !!totals.feesAndTaxes.amount;
  const showEventFeesBlock = currentEventFees.length > 0;

  const totalSummary = (
    <div className="checkout-pricing-details">
      <PricingDetails>
        <PricingDetailsItem title={mainMessages.subtotalAmount} price={totals.faceValue} data-testid="face-value" />
        {showFeesTitle ? (
          <PricingDetailsItem
            title={mainMessages.feesAndTaxes}
            charges={totals.feesAndTaxesBreakdown}
            price={totals.feesAndTaxes}
          />
        ) : null}
        {showEventFeesBlock ? (
          <PricingDetailsItem
            title={messages.otherCharges}
            charges={totals.eventFeesBreakdown}
            price={totals.eventFees}
            editFn={isOrderProcessed ? null : toggleEditChargesModal}
            tooltipMessage={messages.editOtherCharges}
          />
        ) : null}
        <PricingDetailsItem
          title={mainMessages.grandTotalAmount}
          price={totals.total}
          variant="large"
          data-testid="grand-total"
        />
      </PricingDetails>
    </div>
  );

  const eventLists = Object.keys(items).map((eventId) => {
    const event = items[eventId].details;

    const eventDetailsMarkup = (
      <>
        <EventInfo
          header="sdr-checkout"
          name={event.name}
          weekday={event.weekday}
          time={event.time}
          fullDate={event.fullDateShortMonth}
        />
        <div className="sdr-checkout__summary__event--location">{event.location}</div>
      </>
    );

    const sections = isOrderProcessed ? checkoutSeatBlocks[eventId] : eventSeatBlocks[eventId];

    return (
      <div className="checkout-summary-wrap" key={eventId}>
        <div className="checkout-summary">
          {eventDetailsMarkup}
          <div className="checkout__line-break" />
          <div className="sdr-shopping-bag__items">
            {sections.map((section) =>
              section.seats.map((seat: DeliverySeatWithPromotion) => (
                <ShoppingBagItem
                  key={`${seat.name}_${seat.row}_${seat.section}`}
                  section={seat.section}
                  row={seat.row}
                  seat={seat.name}
                  deSelectSeatAction={onDeselectSeat}
                  placeId={seat.generalAdmission ? seat.gaID : seat.placeId}
                  tickettype={seat.type.current.name}
                  promotion={seat.type.current.promotion}
                  price={seat.price.amount}
                  GA={seat.GA}
                  sellClass={seat.sellClass}
                  adaType={seat.adaType}
                  pricing={seat.pricing}
                  isDeselectingSeat={isDeselectingSeat}
                  removingSeatPlacedId={removingSeatPlacedId}
                  allowDelete={!isOrderProcessed}
                  isOrderUnpaid={isOrderUnpaid}
                />
              )),
            )}
          </div>
        </div>
      </div>
    );
  });

  const clearCartBtn =
    isOrderProcessed || isOrderUnpaid ? null : (
      <ButtonComponent
        onButtonClick={onClearCart}
        className="oc-unbutton outline-none sdr-checkout__summary-clear-cart "
      >
        <FormattedMessage {...messages.clearCart} />
      </ButtonComponent>
    );

  return (
    <div className={checkoutSummaryClassName}>
      <div className="sdr-checkout__summary__header">
        <div className="sdr-checkout__summary__header-title">
          <FormattedMessage {...headerMsg} />
        </div>
        <div>{clearCartBtn}</div>
      </div>
      {eventLists}
      {totalSummary}
    </div>
  );
};
